import React, { useEffect, useState } from "react";
import { Box, Chip, Collapse, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppButton } from "../../../components/form";
import {
  chatTurnJobService,
  isTerminalChatTurnStatus,
  type ChatTurnJobView,
} from "../../../services/chatTurnJobService";
import { formatProgressLabel } from "../utils/chatProgressLabel";

const POLL_MS = 8_000;

interface Props {
  job: ChatTurnJobView;
  onChange: (next: ChatTurnJobView) => void;
  onTerminal: (job: ChatTurnJobView) => void;
}

export default function ChatRunChip({ job, onChange, onTerminal }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const terminal = isTerminalChatTurnStatus(job.status);

  useEffect(() => {
    if (terminal) return undefined;
    const poll = setInterval(() => {
      void chatTurnJobService
        .get(job.turnId)
        .then((next) => {
          onChange(next);
          if (isTerminalChatTurnStatus(next.status)) onTerminal(next);
        })
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(poll);
  }, [job.turnId, terminal, onChange, onTerminal]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const next = await chatTurnJobService.cancel(job.turnId);
      onChange(next);
      onTerminal(next);
    } catch {
      // chip stays; poll will refresh
    } finally {
      setCancelling(false);
    }
  };

  const question = (job.question || "").trim();
  const label = question.length > 42 ? `${question.slice(0, 42)}…` : question || t("chat.runChip.untitled");
  const latest = [...(job.timeline || [])].reverse().find((entry) => entry.step === job.step) || job.timeline?.at(-1);
  const stepLabel = job.step
    ? formatProgressLabel(t, job.step, job.tool || latest?.tool, latest?.detail)
    : t("chat.thinking");

  return (
    <Paper
      elevation={3}
      data-testid="chat-run-chip"
      sx={{
        p: 1,
        minWidth: 220,
        maxWidth: 320,
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Chip size="small" color="info" label={t(`chat.runChip.status.${job.status}`)} />
        <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }} title={question}>
          {label}
        </Typography>
        {!terminal && (
          <AppButton
            size="small"
            color="error"
            variant="text"
            disabled={cancelling || job.cancelRequested}
            onClick={() => void handleCancel()}
          >
            {t("chat.runChip.cancel")}
          </AppButton>
        )}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5, cursor: "pointer" }}
        onClick={() => setOpen((v) => !v)}
      >
        {stepLabel}
      </Typography>
      <Collapse in={open}>
        <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {(job.timeline || []).map((entry, i) => (
            <Typography key={`${entry.step}-${i}`} component="li" variant="caption" color="text.secondary">
              {formatProgressLabel(t, entry.step, entry.tool, entry.detail)}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
